type EstateLinkedResource = {
  id: string;
  estateId: string;
  orgId?: string;
};

type GetTeamEstateRestrictionsProps = {
  teamLimitExceeded: boolean;
  currentOrgId?: string | null;
  maps: EstateLinkedResource[];
  projects: EstateLinkedResource[];
  estateKeysNotAllowed: string[];
  mapKeysNotAllowed: string[];
};

/**
 * Combines the normal subscription restrictions with the UI-only team restriction.
 * When the team limit is exceeded, one stable estate remains available to the
 * current user. Estates in the current organization are preferred over external
 * estates, and the estate id provides a stable tie-breaker.
 */
const getTeamEstateRestrictions = ({
  teamLimitExceeded,
  currentOrgId,
  maps,
  projects,
  estateKeysNotAllowed,
  mapKeysNotAllowed,
}: GetTeamEstateRestrictionsProps) => {
  const restrictedEstateIds = new Set(estateKeysNotAllowed);
  const restrictedMapIds = new Set(mapKeysNotAllowed);

  if (teamLimitExceeded) {
    const accessibleEstates = new Map<string, { belongsToCurrentOrg: boolean }>();

    [...maps, ...projects].forEach((resource) => {
      if (!resource.estateId) return;
      const belongsToCurrentOrg = !!currentOrgId && resource.orgId === currentOrgId;
      const current = accessibleEstates.get(resource.estateId);
      accessibleEstates.set(resource.estateId, {
        belongsToCurrentOrg: current?.belongsToCurrentOrg || belongsToCurrentOrg,
      });
    });

    const allowedEstateId = [...accessibleEstates.entries()]
      .filter(([estateId]) => !restrictedEstateIds.has(estateId))
      .sort(([estateIdA, estateA], [estateIdB, estateB]) => {
        if (estateA.belongsToCurrentOrg !== estateB.belongsToCurrentOrg) {
          return estateA.belongsToCurrentOrg ? -1 : 1;
        }
        return estateIdA.localeCompare(estateIdB);
      })[0]?.[0];

    accessibleEstates.forEach((_estate, estateId) => {
      if (estateId !== allowedEstateId) restrictedEstateIds.add(estateId);
    });
  }

  return {
    estates: [...restrictedEstateIds].sort(),
    maps: [...restrictedMapIds].sort(),
  };
};

export default getTeamEstateRestrictions;
